import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useDesignStore } from '../store/designStore';
import { safeGetItem } from '../lib/safeStorage';
import { importCatalogAssetAsUpload } from '../components/tools/StagedImage';
import { FULL_COLOR, type ThinLineCheckResult } from '@customedge/shared';
import type { DesignSide } from '../store/designStore';

// Client-requested FastEditor-parity feature: flags placed artwork whose
// narrowest stroke would be too thin to print, mirroring the reference
// screenshots (a top warning banner + "See details" modal with red dots at
// the offending points). Covers Upload/Clipart/Shapes (all raster images
// stamped with `originalUploadId` at placement — see BackgroundColorTool.tsx's
// commitToCanvas) and Background (see below — a genuinely different source,
// not a canvas object at all). Deliberately does NOT cover live text objects,
// per direct scope decision — text's stroke width is already directly
// editable in PropertyPanel, so there's no HIDDEN thin line to surface there
// the way there is for a raster image's baked-in pixel content.

export interface ThinLineFlag {
  uid: string;          // object's own __uid (LayerPanel's convention) for a normal object; 'background' for the background
  isBackground: boolean;
  result: ThinLineCheckResult;
  // Displayable image URL for the details modal's thumbnail — the object's
  // own originalUploadUrl for a placed image, or the background's source URL.
  previewUrl: string;
  // The live Fabric object itself, for the layer badge's click-to-select —
  // undefined for the background (nothing to select on canvas).
  object?: any;
}

export interface ThinLineCheckState {
  // Whether the ACTIVE side currently has any flagged object or background —
  // what the top banner/badges key off of.
  hasAnyFlag: boolean;
  flags: ThinLineFlag[];
  // True while any check is in flight — callers can use this to avoid
  // flashing a "no problems" state before the first check has even run.
  checking: boolean;
}

// Drops a cached result for `uploadId` so the next scan re-fetches it —
// needed because `originalUploadId` deliberately does NOT change on a
// "Correct Colors"/re-edit pass through BackgroundColorTool.tsx's
// commitToCanvas EXISTING-OBJECT path (see that file's own comment on why:
// order fulfillment needs originalUploadId to keep pointing at the true
// pristine upload) — but the backend's readBestSourceRaster ALWAYS reads
// the LATEST processed variant for a given id with no caching of its own,
// so a stale FRONTEND cache entry is the only thing standing between a
// re-edited image and a fresh check. Called directly by
// BackgroundColorTool.tsx right after committing an existing-object edit.
//
// Also marks `uploadId` in reeditUploadIds (see its own comment) — this
// function is the ONE place in the whole app that actually knows "this
// upload was just re-edited, not freshly uploaded," since it's called from
// the exact same commit path as the re-edit itself. The next
// fetchThinLineCheck for this id reads and consumes that mark to ask the
// backend for the re-edited result instead of the pristine original — see
// that function's own comment and imageProcessing.ts's matching
// 2026-08-20 update to resolveThinLineCheckSource for the full story.
export function invalidateThinLineCheck(uploadId: string | undefined | null) {
  if (!uploadId) return;
  resultCache.delete(uploadId);
  reeditUploadIds.add(uploadId);
}

// See invalidateThinLineCheck's own comment — populated there, consumed
// (read-and-cleared) by fetchThinLineCheck below.
const reeditUploadIds = new Set<string>();

function authHeaders(): HeadersInit {
  const token = safeGetItem('session', 'customedge_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchThinLineCheck(uploadId: string): Promise<ThinLineCheckResult | null> {
  const isReedit = reeditUploadIds.delete(uploadId); // read-and-clear: only THIS fetch is the re-edit's own fresh check
  try {
    const res = await fetch(`/api/images/${uploadId}/thin-line-check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(isReedit ? { reedit: true } : {}),
    });
    if (!res.ok) return null;
    return (await res.json()) as ThinLineCheckResult;
  } catch {
    return null;
  }
}

// Results are cached by originalUploadId for placed objects — the SAME
// upload can be placed on multiple objects/sides (e.g. a clipart added
// twice), and re-checking identical source art on every one would be wasted
// work; a re-placed/replaced object naturally gets a fresh originalUploadId
// anyway (see BackgroundColorTool.tsx's own commitToCanvas comment on when
// it's rewritten), so this cache never goes stale on its own.
const resultCache = new Map<string, ThinLineCheckResult>();

// Module-level (not per-hook-instance) in-flight tracking — this hook is
// mounted independently in 3 places at once (DesignerApp.tsx, LayerPanel.tsx,
// BackgroundTool.tsx), each previously with its OWN `inFlightUploadIds` ref.
// Found live 2026-08-20 (round-4 verification of the reedit fix above): right
// after invalidateThinLineCheck() clears resultCache + marks reeditUploadIds
// for an id, all 3 mounted instances' scan() effects see "not cached, not in
// MY OWN in-flight set" in the same tick and each independently call
// fetchThinLineCheck for the identical id — none see each other's in-flight
// state, since that was per-instance. Only ONE of those calls actually
// consumes the one-shot reeditUploadIds marker (Set.delete is atomic per
// call); the rest silently get the non-reedit {} request instead, and
// whichever one's response lands LAST wins the shared resultCache — so the
// UI could end up showing the stale pre-edit verdict even though the correct
// reedit-aware check ran and returned the right answer moments earlier.
// Fixed by making in-flight tracking module-level too (a shared in-flight
// PROMISE, not just a boolean flag) — now only one fetch is ever issued per
// uploadId at a time, and every caller (regardless of which component
// instance) awaits and reacts to that SAME promise/result, eliminating the
// race entirely rather than just narrowing its window.
const inFlightFetches = new Map<string, Promise<ThinLineCheckResult | null>>();
// Callbacks to notify (their own setTick) once a shared in-flight fetch
// resolves — every hook instance that observed the fetch starting registers
// itself here so it re-renders from the final result, even though it wasn't
// the instance that actually issued the request.
const inFlightListeners = new Map<string, Set<() => void>>();

// Background images are NOT UploadedImage-backed the way a placed object is
// — BackgroundTool.tsx sets them directly from a catalog asset URL
// (fabric.Image.fromURL), with no originalUploadId anywhere. Checking one
// means first importing that URL the same way ClipartTool.tsx/ShapesTool.tsx
// already do (importCatalogAssetAsUpload, same same-origin SSRF guard) to
// get an id the thin-line endpoint can use — cached by URL here so re-
// selecting the same background doesn't re-import + re-check every time.
const bgUploadIdCache = new Map<string, string>();

// A single shared, frozen empty array for every "no flags" case below —
// `flags: []` allocated fresh in the render body gave every consumer a NEW
// array reference on every single render regardless of whether anything
// actually changed. LayerPanel.tsx's buildLayers (a useCallback) closes over
// `thinLineCheck.flags`, so a new reference every render made buildLayers
// itself get a new identity every render, which re-ran the effect that
// re-subscribes to canvas events AND calls buildLayers() synchronously on
// every run — an unconditional setState-in-render-adjacent-effect loop,
// confirmed live as a "Maximum update depth exceeded" warning firing on a
// bare page load with zero user interaction.
const EMPTY_FLAGS: ThinLineFlag[] = [];

export function useThinLineCheck(side: DesignSide | undefined | null): ThinLineCheckState {
  const { canvasInstances } = useDesignStore();
  const canvas = side ? canvasInstances.get(side.sideKey) : undefined;
  const [tick, setTick] = useState(0);
  const [checking, setChecking] = useState(false);
  // Still per-instance — only used to decide THIS instance's own `checking`
  // flag (whether to show a loading state), not for request dedup anymore
  // (that's inFlightFetches/inFlightListeners above, shared across instances).
  const inFlightUploadIds = useRef(new Set<string>());
  const inFlightBgUrls = useRef(new Set<string>());

  const checkUploadId = useCallback((uploadId: string) => {
    if (resultCache.has(uploadId) || inFlightUploadIds.current.has(uploadId)) return;
    inFlightUploadIds.current.add(uploadId);
    setChecking(true);

    let promise = inFlightFetches.get(uploadId);
    if (!promise) {
      // This instance is the one that actually issues the request — every
      // OTHER instance that calls checkUploadId for the same id while this
      // is in flight (see the `else` branch below) reuses this exact promise
      // instead of issuing its own, which is what closes the race described
      // in inFlightFetches' own comment above.
      promise = fetchThinLineCheck(uploadId).finally(() => {
        inFlightFetches.delete(uploadId);
      });
      inFlightFetches.set(uploadId, promise);
    }

    // Register for notification regardless of who issued the fetch — every
    // instance observing this uploadId needs to re-render once the shared
    // result lands, not just the one that happened to issue the request.
    if (!inFlightListeners.has(uploadId)) inFlightListeners.set(uploadId, new Set());
    const notify = () => {
      inFlightUploadIds.current.delete(uploadId);
      setChecking(inFlightUploadIds.current.size > 0 || inFlightBgUrls.current.size > 0);
      setTick(n => n + 1);
    };
    inFlightListeners.get(uploadId)!.add(notify);

    promise.then(result => {
      // Only the FIRST resolution actually writes resultCache — by the time
      // this .then() runs, `result` already reflects whichever single fetch
      // was actually issued (see above), so every listener sees the same
      // final answer; guarding with resultCache.has avoids a harmless but
      // pointless re-write if this listener's own .then() somehow runs after
      // another listener's already stored it.
      if (result && !resultCache.has(uploadId)) resultCache.set(uploadId, result);
      inFlightListeners.get(uploadId)?.delete(notify);
      notify();
    });
  }, []);

  const checkBackgroundUrl = useCallback((url: string) => {
    const cachedUploadId = bgUploadIdCache.get(url);
    if (cachedUploadId) { checkUploadId(cachedUploadId); return; }
    if (inFlightBgUrls.current.has(url)) return;
    inFlightBgUrls.current.add(url);
    setChecking(true);
    importCatalogAssetAsUpload(url).then(async staged => {
      inFlightBgUrls.current.delete(url);
      if (!staged) { setChecking(inFlightUploadIds.current.size > 0 || inFlightBgUrls.current.size > 0); return; }
      bgUploadIdCache.set(url, staged.uploadId);
      checkUploadId(staged.uploadId);
    });
  }, [checkUploadId]);

  // Walks the active canvas's placed image objects (skipping anything
  // without a stable originalUploadId — e.g. text, or an image mid-wizard
  // that hasn't committed yet) plus the current background image (if any),
  // and kicks off a check for anything not already cached. Background
  // polling (not events) mirrors useColorBudget.ts's own identical
  // "canvas.setBackgroundImage() fires no Fabric event" workaround.
  useEffect(() => {
    if (!canvas) return;
    const scan = () => {
      const objects: any[] = (canvas as any).getObjects?.() ?? [];
      for (const obj of objects) {
        if (obj.isTemplateBackground) continue;
        if (obj.type !== 'image') continue; // text/shape-as-vector-path has no baked-in raster content to measure
        const originalUploadId = obj.originalUploadId as string | undefined;
        if (!originalUploadId) continue;
        if (!obj.__uid) obj.__uid = `obj-${Date.now()}-${Math.random()}`;
        checkUploadId(originalUploadId);
      }
      const bgUrl: string | undefined = (canvas as any).backgroundImageSourceUrl;
      if (bgUrl) checkBackgroundUrl(bgUrl);
    };
    scan();
    canvas.on('object:added', scan);
    canvas.on('object:modified', scan);
    const bgPoll = window.setInterval(scan, 1000);
    return () => {
      canvas.off('object:added', scan);
      canvas.off('object:modified', scan);
      window.clearInterval(bgPoll);
    };
  }, [canvas, checkUploadId, checkBackgroundUrl]);

  // Full Color exemption — per direct user framing ("I guess all Full color
  // will support for thin lines"): a print method with no color-count
  // ceiling is treated as not constrained by minimum print detail either, so
  // the check is skipped entirely for this side, same countKey signal
  // useColorBudget.ts already computes for the identical purpose.
  const countKey = side ? useDesignStore.getState().resolveActiveColorCountKey(side) : null;
  const skip = !side || !canvas || countKey === FULL_COLOR;

  // Recomputed only when `tick` bumps (a real result just landed — see
  // checkUploadId/checkBackgroundUrl's own `notify`) or the canvas/side
  // identity changes — NOT on every render, so this hook hands back the
  // exact same `flags` array reference across renders where nothing
  // relevant changed (see EMPTY_FLAGS' own comment for why that matters).
  // `resultCache`/`bgUploadIdCache` are plain module-level Maps, not
  // reactive state — `tick` is this hook's own proxy for "their contents
  // may have changed," same role it already played before this memoization.
  const flags = useMemo<ThinLineFlag[]>(() => {
    if (skip) return EMPTY_FLAGS;
    const result: ThinLineFlag[] = [];
    const objects: any[] = (canvas as any).getObjects?.() ?? [];
    for (const obj of objects) {
      if (obj.isTemplateBackground || obj.type !== 'image') continue;
      const originalUploadId = obj.originalUploadId as string | undefined;
      if (!originalUploadId) continue;
      const flagResult = resultCache.get(originalUploadId);
      if (flagResult?.hasThinLines) {
        result.push({
          uid: obj.__uid ?? originalUploadId,
          isBackground: false,
          result: flagResult,
          // The check itself (flagResult, above) measures whatever the
          // backend's readBestSourceRaster actually read for this id — the
          // pristine original on a first/fresh-upload check, or the latest
          // processed variant (post color-quantize, etc.) on a re-check
          // (see resolveThinLineCheckSource's own comment). The modal's
          // thumbnail must show the SAME content the numbers were computed
          // against, or the red dots land on a different image than the one
          // they were measured from. obj.getSrc() is the object's actual
          // current rendered image (== the quantized `previewUrl` this
          // object was placed with, once BackgroundColorTool.tsx's
          // commitToCanvas has run) — obj.originalUploadUrl is the
          // deliberately-frozen pristine-original archival pointer (kept for
          // order fulfillment/re-edit restaging, see that file's own
          // comment) and must never be used here, since it can silently
          // disagree with what was actually just measured.
          previewUrl: obj.getSrc?.() ?? obj.originalUploadUrl ?? '',
          object: obj,
        });
      }
    }
    const bgUrl: string | undefined = (canvas as any).backgroundImageSourceUrl;
    if (bgUrl) {
      const bgUploadId = bgUploadIdCache.get(bgUrl);
      const bgResult = bgUploadId ? resultCache.get(bgUploadId) : undefined;
      if (bgResult?.hasThinLines) result.push({ uid: 'background', isBackground: true, result: bgResult, previewUrl: bgUrl });
    }
    return result.length > 0 ? result : EMPTY_FLAGS;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, canvas, tick]);

  if (skip) {
    return { hasAnyFlag: false, flags: EMPTY_FLAGS, checking: false };
  }

  return { hasAnyFlag: flags.length > 0, flags, checking };
}
